// Global per-user preferences: defaults for tables, number/date formatting, interface. Mirrored to
// the backend (/preferences key "app.prefs") with a localStorage cache for instant, offline load.
// Read synchronously with getPrefs() (formatters use it); subscribe in React with usePrefs().

import { useSyncExternalStore } from "react";
import { api } from "./api";

export type Density = "compact" | "default" | "comfortable";
export type Period = "7" | "30" | "month" | "lastmonth" | "all";
export type DateFmt = "dd.mm.yyyy" | "yyyy-mm-dd" | "d-mon-yyyy";

export interface Prefs {
  density: Density;
  defaultPeriod: Period;
  pageSize: number;
  groupSep: boolean; // 1 234 567 vs 1234567
  moneyDecimals: 0 | 1 | 2;
  moneyUnit: "auto" | "full"; // auto: 1.20 млрд · full: 1 200 000 000
  dateFmt: DateFmt;
  wrap: boolean; // wrap long cell text instead of truncating
  tableStripes: boolean; // alternate row shading in flat tables
  sideCollapsed: boolean; // sidebar starts collapsed
  ovKpis: string[]; // which KPI tiles show on the Обзор page, in order
  ovCards: string[]; // which Обзор sections show, in order
  ovTrendDays: number; // Обзор movement chart window
  ovCatMetric: "units" | "value"; // Обзор category chart metric
  ovRev: number; // Обзор layout revision — lets new cards reach users who already saved a layout
}

export const OV_KPIS = ["stock", "available", "cover", "stock_value", "sku", "problems"] as const;
export const OV_KPI_LABEL: Record<string, string> = {
  stock: "Остаток на складе",
  available: "Доступно к отправке",
  cover: "Запас в днях",
  stock_value: "Себестоимость остатка",
  sku: "Товарных позиций",
  problems: "Проблемы",
};

export const OV_CARDS = [
  "kpis",
  "movement",
  "trend",
  "stock_health",
  "stockout_risk",
  "calendar",
  "today",
  "top_shipped",
  "top_supplied",
  "dead_stock",
  "shipment_categories",
  "categories",
  "reconciliation",
] as const;
export const OV_CARD_LABEL: Record<string, string> = {
  kpis: "Ключевые показатели",
  movement: "Движение за период (итоги)",
  trend: "График движения",
  stock_health: "Здоровье запасов (возраст, ABC)",
  stockout_risk: "Риск дефицита",
  calendar: "Календарь активности",
  today: "Последний день",
  top_shipped: "Больше всего отгружено",
  top_supplied: "Больше всего поступило",
  dead_stock: "Залежавшийся товар",
  shipment_categories: "Отгрузка по категориям (30 дней)",
  categories: "Остаток по категориям",
  reconciliation: "Сверка остатков",
};

export const DEFAULT_PREFS: Prefs = {
  density: "default",
  defaultPeriod: "30",
  pageSize: 50,
  groupSep: true,
  moneyDecimals: 2,
  moneyUnit: "auto",
  dateFmt: "dd.mm.yyyy",
  wrap: false,
  tableStripes: false,
  sideCollapsed: false,
  ovKpis: ["stock", "available", "cover", "stock_value", "sku", "problems"],
  ovCards: [
    "kpis",
    "movement",
    "trend",
    "stock_health",
    "stockout_risk",
    "dead_stock",
    "today",
    "top_shipped",
    "top_supplied",
    "shipment_categories",
    "categories",
    "calendar",
  ],
  ovTrendDays: 30,
  ovCatMetric: "units",
  ovRev: 4,
};

/** Layout rev 2 (2026-09-28) added the stock-health / stockout-risk cards and the «Запас в днях»
 * KPI. A layout saved before that gets them inserted once (after «trend» / «available»); later the
 * user's own choice — including hiding them — sticks. */
function migrateOverview(cards: string[], kpis: string[], rev: number): { cards: string[]; kpis: string[] } {
  if (rev >= 4) return { cards, kpis };
  const m = migrateOverviewV3(cards, kpis, rev);
  // rev 4 (2026-09-28): the default layout is the owner's own arrangement — a saved layout gets the
  // default sections it lacks (e.g. «Больше всего поступило») once, each after its default predecessor.
  const c = [...m.cards];
  const order = DEFAULT_PREFS.ovCards;
  order.forEach((id, i) => {
    if (c.includes(id)) return;
    const prev = order.slice(0, i).reverse().find((x) => c.includes(x));
    c.splice(prev ? c.indexOf(prev) + 1 : 0, 0, id);
  });
  return { cards: c, kpis: m.kpis };
}

function migrateOverviewV3(cards: string[], kpis: string[], rev: number): { cards: string[]; kpis: string[] } {
  if (rev >= 3) return { cards, kpis };
  if (rev === 2) {
    // rev 3 (2026-09-28): «Календарь активности» — appended after «dead_stock» (or at the end)
    if (cards.includes("calendar")) return { cards, kpis };
    const c = [...cards];
    const at = c.indexOf("dead_stock");
    c.splice(at >= 0 ? at + 1 : c.length, 0, "calendar");
    return { cards: c, kpis };
  }
  const c = cards.filter((x) => x !== "stock_health" && x !== "stockout_risk");
  const at = c.indexOf("trend");
  c.splice(at >= 0 ? at + 1 : c.length, 0, "stock_health", "stockout_risk");
  if (!c.includes("calendar")) c.push("calendar");
  const k = [...kpis];
  if (!k.includes("cover")) {
    const ai = k.indexOf("available");
    k.splice(ai >= 0 ? ai + 1 : k.length, 0, "cover");
  }
  return { cards: c, kpis: k };
}

const KEY = "wa_prefs";
const PREF_KEY = "app.prefs";

function sanitize(raw: unknown): Prefs {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_PREFS };
  const r = raw as Partial<Prefs>;
  const pick = <K extends keyof Prefs>(k: K, ok: (v: unknown) => boolean): Prefs[K] =>
    ok(r[k]) ? (r[k] as Prefs[K]) : DEFAULT_PREFS[k];
  const ovKpis =
    Array.isArray(r.ovKpis) && r.ovKpis.every((x) => OV_KPIS.includes(x as (typeof OV_KPIS)[number])) && r.ovKpis.length
      ? (r.ovKpis as string[])
      : DEFAULT_PREFS.ovKpis;
  const ovCards =
    Array.isArray(r.ovCards) && r.ovCards.every((x) => OV_CARDS.includes(x as (typeof OV_CARDS)[number])) && r.ovCards.length
      ? (r.ovCards as string[])
      : DEFAULT_PREFS.ovCards;
  const mig = migrateOverview(ovCards, ovKpis, typeof r.ovRev === "number" ? r.ovRev : 0);
  return {
    density: pick("density", (v) => v === "compact" || v === "default" || v === "comfortable"),
    defaultPeriod: pick("defaultPeriod", (v) => ["7", "30", "month", "lastmonth", "all"].includes(v as string)),
    pageSize: pick("pageSize", (v) => typeof v === "number" && v >= 10 && v <= 1000),
    groupSep: pick("groupSep", (v) => typeof v === "boolean"),
    moneyDecimals: pick("moneyDecimals", (v) => v === 0 || v === 1 || v === 2),
    moneyUnit: pick("moneyUnit", (v) => v === "auto" || v === "full"),
    dateFmt: pick("dateFmt", (v) => ["dd.mm.yyyy", "yyyy-mm-dd", "d-mon-yyyy"].includes(v as string)),
    wrap: pick("wrap", (v) => typeof v === "boolean"),
    tableStripes: pick("tableStripes", (v) => typeof v === "boolean"),
    sideCollapsed: pick("sideCollapsed", (v) => typeof v === "boolean"),
    ovKpis: mig.kpis,
    ovCards: mig.cards,
    ovTrendDays: pick("ovTrendDays", (v) => typeof v === "number" && [7, 14, 30, 60, 90].includes(v)),
    ovCatMetric: pick("ovCatMetric", (v) => v === "units" || v === "value"),
    ovRev: 4,
  };
}

let current: Prefs = (() => {
  try {
    return sanitize(JSON.parse(localStorage.getItem(KEY) ?? "null"));
  } catch {
    return { ...DEFAULT_PREFS };
  }
})();

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function getPrefs(): Prefs {
  return current;
}

let saveTimer: ReturnType<typeof setTimeout> | undefined;
export function setPrefs(patch: Partial<Prefs>): void {
  current = sanitize({ ...current, ...patch });
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* ignore */
  }
  emit();
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    api.put(`/preferences/${PREF_KEY}`, { value: current }).catch(() => undefined);
  }, 600);
}

export function resetPrefs(): void {
  setPrefs({ ...DEFAULT_PREFS });
}

/** Pull the server copy once on app start (falls back silently to the local cache). */
export async function hydratePrefs(): Promise<void> {
  try {
    const res = await api.get<Record<string, unknown>>("/preferences");
    const server = res.data[PREF_KEY];
    if (server) {
      current = sanitize(server);
      try {
        localStorage.setItem(KEY, JSON.stringify(current));
      } catch {
        /* ignore */
      }
      emit();
    }
  } catch {
    /* offline / auth off */
  }
}

export function usePrefs(): Prefs {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getPrefs,
    getPrefs,
  );
}

const MONTHS = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

/** "2026-09-09" -> formatted per prefs.dateFmt */
export function formatDateStr(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  switch (current.dateFmt) {
    case "yyyy-mm-dd":
      return `${y}-${m}-${d}`;
    case "d-mon-yyyy":
      return `${Number(d)} ${MONTHS[Number(m) - 1] ?? m} ${y}`;
    default:
      return `${d}.${m}.${y}`;
  }
}
